// TODO: legal review required before public launch.
// Fill in [COMPANY_NAME], [ADDRESS],
// [SUPERVISORY_AUTHORITY] placeholders in the translation files (legal.privacy.*).

import { Trans, useTranslation } from "react-i18next";

import { LegalPageLayout, LegalSection } from "@/components/layout/LegalPageLayout";

const PROCESSORS: Array<{
  key: string;
  defaultName: string;
  defaultData: string;
  defaultPurpose: string;
  defaultRegion: string;
}> = [
  {
    key: "aiServices",
    defaultName: "External AI service providers",
    defaultData: "Exam answers and, where needed for speaking practice, audio recordings.",
    defaultPurpose: "Answer analysis, automated feedback, and speech-to-text support.",
    defaultRegion: "European Economic Area and third countries — transfers outside the EEA are protected by EU Standard Contractual Clauses (SCCs) and/or an adequacy decision where applicable.",
  },
  {
    key: "serviceOperations",
    defaultName: "Service hosting, support, and security providers",
    defaultData: "Account, usage, support, and service data needed to operate and protect the Service.",
    defaultPurpose: "Hosting, service delivery, troubleshooting, abuse prevention, backups, and secure operations.",
    defaultRegion: "European Economic Area and, where necessary, third countries protected by EU Standard Contractual Clauses (SCCs) and/or an adequacy decision where applicable.",
  },
];

const DATA_CATEGORIES: Array<{ key: string; defaultText: string }> = [
  { key: "account", defaultText: "Account data: email address, password credentials stored securely, display name, CEFR level, language preference." },
  { key: "exam", defaultText: "Exam content: your written and spoken answers to reading, writing, listening and speaking practice items." },
  { key: "audio", defaultText: "Audio recordings: voice recordings captured during speaking practice (only when you have enabled voice-recording consent)." },
  { key: "consent", defaultText: "Consent records: which consents you granted or withdrew, the applicable policy version, and the date and time of your choice." },
  { key: "partner", defaultText: "Partner-Finder profile: information you choose to make discoverable to other learners (e.g. display name, CEFR level, learning goals, language preferences) so other users can find and contact you." },
  { key: "chat", defaultText: "User-to-user chat: messages exchanged with other users through the in-app chat. Message content is transmitted and stored in encrypted form. We process only the minimum metadata required to route, deliver, and order conversations (sender, recipient, timestamp, conversation ID). Attachments, voice notes, video, and file sharing are not supported." },
  { key: "usage", defaultText: "Usage and security data: IP address, approximate browser and device information, activity times, and error reports." },
];

const RIGHTS: Array<{ key: string; defaultText: string }> = [
  { key: "access", defaultText: "Right of access (Art. 15) — request a copy of the personal data we hold about you." },
  { key: "rectification", defaultText: "Right to rectification (Art. 16) — correct inaccurate or incomplete data." },
  { key: "erasure", defaultText: "Right to erasure (Art. 17) — request deletion of your data ('right to be forgotten')." },
  { key: "restriction", defaultText: "Right to restriction of processing (Art. 18)." },
  { key: "portability", defaultText: "Right to data portability (Art. 20) — receive your data in a structured, machine-readable format." },
  { key: "object", defaultText: "Right to object (Art. 21) to processing based on legitimate interests." },
  { key: "withdraw", defaultText: "Right to withdraw consent at any time (Art. 7(3)). Withdrawal does not affect prior lawful processing." },
  { key: "complaint", defaultText: "Right to lodge a complaint with a supervisory authority (Art. 77), e.g. [SUPERVISORY_AUTHORITY]." },
];

export default function PrivacyPolicy() {
  const { t } = useTranslation();

  return (
    <LegalPageLayout
      title={t("legal.privacy.title", { defaultValue: "Privacy Policy" })}
      lastUpdated={t("legal.privacy.lastUpdated", { defaultValue: "2026-05-18" })}
    >
      <p>
        {t("legal.privacy.intro", {
          defaultValue:
            "This Privacy Policy explains how DeutschGenie ('we', 'us') collects, uses, stores and shares personal data when you use our website and services. We process personal data in accordance with the EU General Data Protection Regulation (GDPR) and the German Federal Data Protection Act (BDSG).",
        })}
      </p>

      <LegalSection id="controller" title={t("legal.privacy.sections.controller.title", { defaultValue: "1. Data Controller" })}>
        <p>
          {t("legal.privacy.sections.controller.body", {
            defaultValue:
              "The controller responsible for processing your personal data under Art. 4(7) GDPR is:",
          })}
        </p>
        <address className="not-italic rounded-md border border-border bg-muted/50 px-4 py-3 text-sm leading-relaxed">
          <strong className="block font-ui text-foreground">[COMPANY_NAME]</strong>
          <span className="block text-muted-foreground">[ADDRESS]</span>
          <span className="block text-muted-foreground">
            {t("legal.privacy.sections.controller.emailLabel", { defaultValue: "Email" })}:{" "}
            <a href="mailto:deutschgenie.contact@gmail.com" className="text-primary hover:underline">
              deutschgenie.contact@gmail.com
            </a>
          </span>
          <span className="block text-muted-foreground">
            {t("legal.privacy.sections.controller.dpoLabel", { defaultValue: "Data Protection Officer" })}:{" "}
            <a href="mailto:deutschgenie.contact@gmail.com" className="text-primary hover:underline">
              deutschgenie.contact@gmail.com
            </a>
          </span>
        </address>
      </LegalSection>

      <LegalSection id="data" title={t("legal.privacy.sections.data.title", { defaultValue: "2. Categories of Personal Data We Process" })}>
        <p>
          {t("legal.privacy.sections.data.body", {
            defaultValue: "We process the following categories of personal data:",
          })}
        </p>
        <ul className="list-disc pl-6 space-y-2">
          {DATA_CATEGORIES.map((item) => (
            <li key={item.key}>
              {t(`legal.privacy.sections.data.items.${item.key}`, { defaultValue: item.defaultText })}
            </li>
          ))}
        </ul>
        <p>
          {t("legal.privacy.sections.data.required", {
            defaultValue:
              "Providing account and practice data is generally voluntary, but some information is necessary to create an account, deliver the Service, provide feedback, prevent abuse, or comply with law. If you do not provide required information, the affected feature may not be available.",
          })}
        </p>
      </LegalSection>

      <LegalSection id="purposes" title={t("legal.privacy.sections.purposes.title", { defaultValue: "3. Purposes and Legal Bases" })}>
        <p>
          {t("legal.privacy.sections.purposes.intro", {
            defaultValue: "We process your personal data on the following legal bases under Art. 6(1) GDPR:",
          })}
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>{t("legal.privacy.sections.purposes.contract.label", { defaultValue: "Performance of a contract (Art. 6(1)(b))" })}:</strong>{" "}
            {t("legal.privacy.sections.purposes.contract.body", {
              defaultValue: "to create and operate your account, deliver exam-preparation content, store progress, and provide the Partner Finder and user-to-user chat features, including the secure transmission, encrypted storage, and timely deletion of messages.",
            })}
          </li>
          <li>
            <strong>{t("legal.privacy.sections.purposes.consent.label", { defaultValue: "Consent (Art. 6(1)(a))" })}:</strong>{" "}
            {t("legal.privacy.sections.purposes.consent.body", {
              defaultValue: "to send your exam answers to third-party AI services for analysis and feedback, and (separately) to record your voice during speaking practice.",
            })}
          </li>
          <li>
            <strong>{t("legal.privacy.sections.purposes.legitimate.label", { defaultValue: "Legitimate interests (Art. 6(1)(f))" })}:</strong>{" "}
            {t("legal.privacy.sections.purposes.legitimate.body", {
              defaultValue: "to protect the Service from abuse, troubleshoot problems, maintain reliability, and improve product quality. You may object to this processing at any time.",
            })}
          </li>
          <li>
            <strong>{t("legal.privacy.sections.purposes.legal.label", { defaultValue: "Legal obligation (Art. 6(1)(c))" })}:</strong>{" "}
            {t("legal.privacy.sections.purposes.legal.body", {
              defaultValue: "where we are required to retain data to comply with statutory record-keeping obligations.",
            })}
          </li>
        </ul>
      </LegalSection>

      <LegalSection id="processors" title={t("legal.privacy.sections.processors.title", { defaultValue: "4. Third-Party Processors and Recipients" })}>
        <p>
          {t("legal.privacy.sections.processors.intro", {
            defaultValue:
              "We share specific personal data with the following processors. Each processor is engaged under a Data Processing Agreement (DPA) pursuant to Art. 28 GDPR.",
          })}
        </p>
        <div className="space-y-4">
          {PROCESSORS.map((processor) => (
            <div key={processor.key} className="rounded-md border border-border bg-muted/50 p-4 space-y-2 text-sm">
              <div className="font-ui font-semibold text-foreground">
                {t(`legal.privacy.sections.processors.${processor.key}.name`, { defaultValue: processor.defaultName })}
              </div>
              <div>
                <span className="font-medium text-foreground/80">
                  {t("legal.privacy.sections.processors.dataLabel", { defaultValue: "Data shared" })}:
                </span>{" "}
                {t(`legal.privacy.sections.processors.${processor.key}.data`, { defaultValue: processor.defaultData })}
              </div>
              <div>
                <span className="font-medium text-foreground/80">
                  {t("legal.privacy.sections.processors.purposeLabel", { defaultValue: "Purpose" })}:
                </span>{" "}
                {t(`legal.privacy.sections.processors.${processor.key}.purpose`, { defaultValue: processor.defaultPurpose })}
              </div>
              <div>
                <span className="font-medium text-foreground/80">
                  {t("legal.privacy.sections.processors.regionLabel", { defaultValue: "Region & transfer mechanism" })}:
                </span>{" "}
                {t(`legal.privacy.sections.processors.${processor.key}.region`, { defaultValue: processor.defaultRegion })}
              </div>
            </div>
          ))}
        </div>
        <p>
          {t("legal.privacy.sections.processors.note", {
            defaultValue:
              "We do not currently use third-party advertising, behavioural tracking, social-media tracking, or payment processors. If we add such services in the future, we will update this policy before they are activated.",
          })}
        </p>
      </LegalSection>

      <LegalSection id="transfers" title={t("legal.privacy.sections.transfers.title", { defaultValue: "5. International Data Transfers" })}>
        <p>
          {t("legal.privacy.sections.transfers.body", {
            defaultValue:
              "Some service providers may process personal data outside the European Economic Area. Where personal data is transferred outside the EEA, we rely on an adequacy decision, the European Commission's Standard Contractual Clauses (SCCs), and/or supplementary safeguards where required. A copy of the safeguards in place can be requested from the controller.",
          })}
        </p>
      </LegalSection>

      <LegalSection id="retention" title={t("legal.privacy.sections.retention.title", { defaultValue: "6. Storage Duration" })}>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            {t("legal.privacy.sections.retention.account", {
              defaultValue: "Account data, exam answers and progress: retained for as long as your account is active, unless a shorter period applies or longer retention is required for legal claims, security, or statutory obligations. You may request account deletion at any time by contacting us.",
            })}
          </li>
          <li>
            {t("legal.privacy.sections.retention.audio", {
              defaultValue: "Voice recordings: automatically deleted after 7 days from the recording date.",
            })}
          </li>
          <li>
            {t("legal.privacy.sections.retention.chat", {
              defaultValue: "User-to-user chat messages: stored in encrypted form for the duration of the conversation. Messages are permanently deleted from our servers when (a) a participant deletes the conversation, (b) a participant deletes their account, or (c) the message exceeds its scheduled retention window. Deletion by one participant removes the conversation copy from our active systems for all participants. Encrypted backups, if any, are overwritten in the ordinary backup cycle and are not used to reconstruct deleted content.",
            })}
          </li>
          <li>
            {t("legal.privacy.sections.retention.consent", {
              defaultValue: "Consent records: retained for as long as your account exists plus the statutory limitation period, in order to demonstrate compliance with Art. 7(1) GDPR.",
            })}
          </li>
          <li>
            {t("legal.privacy.sections.retention.logs", {
              defaultValue: "Security and service records: retained for up to [LOG_RETENTION_DAYS] days for security, reliability, and abuse prevention; older entries are deleted or anonymised.",
            })}
          </li>
        </ul>
      </LegalSection>

      <LegalSection id="cookies" title={t("legal.privacy.sections.cookies.title", { defaultValue: "7. Cookies and Browser Storage" })}>
        <p>
          {t("legal.privacy.sections.cookies.intro", {
            defaultValue: "We use only strictly necessary cookies and browser storage. We do not use tracking, advertising, or analytics cookies.",
          })}
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>{t("legal.privacy.sections.cookies.authLabel", { defaultValue: "Session cookie" })}:</strong>{" "}
            {t("legal.privacy.sections.cookies.authBody", {
              defaultValue: "a strictly necessary cookie used to keep you signed in. It expires when your session ends or after 24 hours, whichever comes first.",
            })}
          </li>
          <li>
            <strong>{t("legal.privacy.sections.cookies.lsLabel", { defaultValue: "Browser storage" })}:</strong>{" "}
            {t("legal.privacy.sections.cookies.lsBody", {
              defaultValue: "we save non-personal preferences and draft progress, such as unfinished exam progress, bookmarks, and language preference, on your device.",
            })}
          </li>
        </ul>
      </LegalSection>

      <LegalSection id="rights" title={t("legal.privacy.sections.rights.title", { defaultValue: "8. Your Rights" })}>
        <p>
          {t("legal.privacy.sections.rights.intro", {
            defaultValue: "Under the GDPR, you have the following rights regarding your personal data:",
          })}
        </p>
        <ul className="list-disc pl-6 space-y-2">
          {RIGHTS.map((right) => (
            <li key={right.key}>
              {t(`legal.privacy.sections.rights.items.${right.key}`, { defaultValue: right.defaultText })}
            </li>
          ))}
        </ul>
        <p>
          <Trans
            i18nKey="legal.privacy.sections.rights.contact"
            defaults="To exercise any of these rights, contact us at <1>deutschgenie.contact@gmail.com</1>. We will respond within one month where required by Art. 12(3) GDPR, may ask for information needed to verify your identity, and may refuse or charge a reasonable fee for manifestly unfounded or excessive requests where Art. 12(5) GDPR allows this."
            components={{
              1: <a href="mailto:deutschgenie.contact@gmail.com" className="text-primary hover:underline" />,
            }}
          />
        </p>
      </LegalSection>

      <LegalSection id="security" title={t("legal.privacy.sections.security.title", { defaultValue: "9. Security Measures" })}>
        <p>
          {t("legal.privacy.sections.security.body", {
            defaultValue:
              "We protect personal data with risk-appropriate technical and organisational measures as required by Art. 32 GDPR, including encryption, access restrictions, monitoring, backup and recovery processes, and review of safeguards where necessary. No online service can be guaranteed to be completely secure.",
          })}
        </p>
      </LegalSection>

      <LegalSection id="children" title={t("legal.privacy.sections.children.title", { defaultValue: "10. Children" })}>
        <p>
          {t("legal.privacy.sections.children.body", {
            defaultValue:
              "DeutschGenie is not intended for children under the age of 16. We do not knowingly collect personal data from children under 16. If you become aware that a child has provided us with personal data, please contact us and we will take appropriate deletion or restriction steps where required by law.",
          })}
        </p>
      </LegalSection>

      <LegalSection id="partner-chat" title={t("legal.privacy.sections.partnerChat.title", { defaultValue: "11. Partner Finder and User-to-User Chat" })}>
        <p>
          {t("legal.privacy.sections.partnerChat.intro", {
            defaultValue:
              "The Partner Finder lets you make a learning profile discoverable to other users. The in-app chat enables limited text-only communication between users who have connected through the Service. The chat is intentionally limited in scope: it does not support attachments, images, voice notes, video, calls, or file sharing.",
          })}
        </p>
        <p>
          {t("legal.privacy.sections.partnerChat.encryption", {
            defaultValue:
              "Chat messages are protected with industry-standard encryption in transit (TLS) and at rest on our servers. We do not actively read, monitor, screen, moderate, or use chat content for training, profiling, advertising, or analytics. Because chats occur between independent users, we cannot guarantee how a recipient will store, copy, screenshot, forward, or further use the messages you send them.",
          })}
        </p>
        <p>
          {t("legal.privacy.sections.partnerChat.deletion", {
            defaultValue:
              "When you delete a conversation, the conversation and its messages are removed from our active systems. When you delete your account, your Partner Finder profile and all chat data linked to your account are removed from our active systems, subject to short-term encrypted backup cycles and any retention strictly required by law. We do not retain shadow copies of deleted chats and do not provide message recovery.",
          })}
        </p>
        <p>
          {t("legal.privacy.sections.partnerChat.controller", {
            defaultValue:
              "When you communicate with another user, that other user receives a copy of your message and becomes an independent controller for any further processing they perform with it (e.g. saving, exporting, or re-sharing). We have no control over and accept no responsibility for the conduct of other users or the way they handle messages you send to them. Use the in-app reporting and blocking tools if another user behaves unlawfully or breaches our Terms of Service.",
          })}
        </p>
        <p>
          {t("legal.privacy.sections.partnerChat.responsibility", {
            defaultValue:
              "You are solely responsible for the content you publish in your Partner Finder profile and send in chat. Do not share special-category data (Art. 9 GDPR), payment data, government IDs, credentials, third-party personal data, or confidential information through the chat. The chat is not a secure channel for sensitive, medical, legal, financial, or safety-critical communications.",
          })}
        </p>
      </LegalSection>

      <LegalSection id="user-responsibility" title={t("legal.privacy.sections.userResponsibility.title", { defaultValue: "12. Your Choices and Responsibilities" })}>
        <p>
          {t("legal.privacy.sections.userResponsibility.body", {
            defaultValue:
              "You are responsible for the personal data and content you choose to submit, including any messages you send to other users. Please do not submit special-category data, third-party personal data, confidential materials, or information you are not authorised to share unless it is necessary for your use of the Service and you have a lawful basis to provide it. Our automated practice feedback is for learning support only and does not create legal, contractual, academic, immigration, or examination decisions with legal or similarly significant effects. Nothing in this section limits your GDPR rights or our mandatory obligations under data protection law.",
          })}
        </p>
      </LegalSection>

      <LegalSection id="changes" title={t("legal.privacy.sections.changes.title", { defaultValue: "13. Changes to this Policy" })}>
        <p>
          {t("legal.privacy.sections.changes.body", {
            defaultValue:
              "We may update this Privacy Policy from time to time to reflect changes in our services or in the law. The current version is identified by the 'last updated' date at the top of this page. Where required by law, we will notify you of material changes or request renewed consent.",
          })}
        </p>
      </LegalSection>
    </LegalPageLayout>
  );
}
